import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Package, Users, Ship, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroData, impactStats, programs, testimonials } from '../data/mock';

const Home = () => {
  const iconMap = {
    Users: Users,
    Ship: Ship,
    Package: Package,
    Handshake: Handshake,
    Heart: Heart,
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroData.heroImage}
            alt="Bridge of Hope Foundation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              {heroData.title}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed">
              {heroData.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/get-involved?tab=donate">
                <Button
                  size="lg"
                  className="bg-[#D4AF37] hover:bg-[#C19B2C] text-white font-semibold px-8 py-6 text-lg shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  {heroData.ctaPrimary}
                </Button>
              </Link>
              <Link to="/our-work">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white font-semibold px-8 py-6 text-lg transition-all duration-300"
                >
                  {heroData.ctaSecondary}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => {
              const Icon = iconMap[stat.icon];
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-sm md:text-base text-gray-200">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">
              How Bridge of Hope Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We collect useful mobility and assistive equipment, coordinate safe shipment, and
              work with trusted partners to get support to individuals and communities in need.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program) => {
              const Icon = iconMap[program.icon];
              return (
                <Card
                  key={program.id}
                  className="border-2 border-gray-100 hover:border-[#2D5F3F] transition-all duration-300 hover:shadow-xl group"
                >
                  <div className="relative h-56 overflow-hidden rounded-t-lg">
                    <img
                      src={program.image}
                      alt={program.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 bg-[#D4AF37] p-3 rounded-xl">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-[#2D5F3F] mb-3">{program.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{program.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials / Impact Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">
              Our Growing Impact
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are building a strong foundation for practical support, trusted partnerships, and
              meaningful long-term impact.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="bg-white border-none shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h4 className="font-bold text-[#2D5F3F]">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.content}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2D5F3F] via-[#3A7D4A] to-[#2D5F3F] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Be Part of the Hope We Deliver</h2>
          <p className="text-xl mb-8 text-gray-200 leading-relaxed">
            Your support helps us collect, ship, and distribute mobility and assistive equipment to
            individuals and communities in need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved?tab=donate">
              <Button
                size="lg"
                className="bg-[#D4AF37] hover:bg-[#C19B2C] text-white font-semibold px-10 py-6 text-lg shadow-xl transition-all duration-300 hover:scale-105"
              >
                <Heart className="w-5 h-5 mr-2" />
                Support the Mission
              </Button>
            </Link>
            <Link to="/about">
              <Button
                size="lg"
                variant="outline"
                className="bg-white text-[#2D5F3F] hover:bg-gray-100 border-2 border-white font-semibold px-10 py-6 text-lg transition-all duration-300"
              >
                Learn More About Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;